import usePagination from "src/customHooks/usePagination";
import PageHeader from "src/components/pageHeader";
import Pagination from "src/components/pagination";
import { Navigate } from "react-router-dom";
import PageLoading from "src/components/pageLoading";
import { Session } from "../types/session.entity";
import { useCountSessionsQuery, useGetSessionsQuery } from "../session.service";
import { FilterSessionsInputs } from "../types";
import { SessionsTable } from "../components/sessions/table";
import { SessionsSearch, SessionsSearchRouteState } from "../components/sessions/search";
import { SessionsTableSkeleton } from "../components/sessions/SessionsTableSkeleton";
import { useCallback, useMemo } from "react";
import { usePaginatedTableSearchParams } from "src/customHooks/usePaginatedTableSearchParams";

const Sessions = () => {
  const tableSearch = usePaginatedTableSearchParams<SessionsSearchRouteState>({
    parseState: (searchParams) => {
      const query: SessionsSearchRouteState = {};
      const learner = searchParams.get("learner");
      const coach = searchParams.get("coach");
      const id = searchParams.get("id");
      const state = searchParams.get("state");
      const type = searchParams.get("type");
      const currency = searchParams.get("currency");
      const space = searchParams.get("space");

      if (learner) query.learner = learner;
      if (coach) query.coach = coach;
      if (id) query.id = id;
      if (state) query.state = state as FilterSessionsInputs["state"];
      if (type) query.type = type as FilterSessionsInputs["type"];
      if (currency) query.currency = currency;
      if (space) query.space = space;

      return query;
    },
    serializeState: (state) => {
      const nextParams = new URLSearchParams();
      if (state.learner) nextParams.set("learner", state.learner);
      if (state.coach) nextParams.set("coach", state.coach);
      if (state.id) nextParams.set("id", state.id);
      if (state.state) nextParams.set("state", state.state);
      if (state.type) nextParams.set("type", state.type);
      if (state.currency) nextParams.set("currency", state.currency);
      if (state.space) nextParams.set("space", state.space);
      return nextParams;
    },
  });

  const routeState = tableSearch.state;
  const initialQuery = useMemo<FilterSessionsInputs>(
    () => ({
      ...(routeState.learner ? { learnerIdentifier: routeState.learner } : {}),
      ...(routeState.coach ? { coachIdentifier: routeState.coach } : {}),
      ...(routeState.id ? { id: routeState.id } : {}),
      ...(routeState.state ? { state: routeState.state } : {}),
      ...(routeState.type ? { type: routeState.type } : {}),
      ...(routeState.currency ? { currency: routeState.currency as FilterSessionsInputs["currency"] } : {}),
      ...(routeState.space ? { langCode: routeState.space } : {}),
    }),
    [routeState.coach, routeState.currency, routeState.id, routeState.learner, routeState.space, routeState.state, routeState.type]
  );

  const { data, numberOfPages, currentPage, next, prev, isSuccess, isError, isLoading, isFetching } = usePagination<Session>({
    useGetDataQuery: useGetSessionsQuery,
    useCountData: useCountSessionsQuery,
    initQuery: initialQuery,
    initPageTokens: tableSearch.pageTokens,
    initCurrentIndex: tableSearch.currentIndex,
    syncKey: tableSearch.syncKey,
    onPaginationStateChange: tableSearch.replacePagination,
  });

  const updateFilterQuery = useCallback(
    (filter: SessionsSearchRouteState) => {
      tableSearch.replaceFilters(filter);
    },
    [tableSearch]
  );
  const showTableSkeleton = !data && (isLoading || isFetching);

  return (
    <div className="page_container">
      <PageHeader text="Sessions" />

      <SessionsSearch updateFilterQuery={updateFilterQuery} initialQuery={routeState} />

      {isSuccess ? (
        <>
          {showTableSkeleton ? (
            <div className="overflow-x-auto mb-6">
              <table className="min-w-full border border-gray-200 divide-y divide-gray-200">
                <thead className="bg-gray-800 text-white text-center">
                  <tr>
                    <th className="px-4 py-3">Id</th>
                    <th className="px-4 py-3">Language</th>
                    <th className="px-4 py-3">Start At</th>
                    <th className="px-4 py-3">Duration Minutes</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">State</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">View</th>
                  </tr>
                </thead>
                <tbody>
                  <SessionsTableSkeleton rows={10} />
                </tbody>
              </table>
            </div>
          ) : (
            <SessionsTable data={data ?? []} />
          )}
          <Pagination numberOfPages={numberOfPages} currentPage={currentPage} prev={prev} next={next} isLoading={isLoading || isFetching} />
        </>
      ) : isError ? (
        <Navigate to="/505" />
      ) : (
        <PageLoading />
      )}
    </div>
  );
};

export default Sessions;
